import axios, { AxiosError } from 'axios';
import {
  ACCESS_TOKEN_COOKIE,
  API_HOST_AUTH,
  ANONYMOUS_ID_STORAGE_KEY
} from '@constants';
import { AddressDraft, Customer, CustomerDraft } from '@commercetools/platform-sdk';
import { appLocalStorage, handleServiceError } from '@utils';
import { mapFullCart } from './mapper';
import { LogInResult, Token } from './interfaces';

let cookies = '';

export const setRequestCookies = (cookieHeader: string): void => {
  cookies = cookieHeader || '';
};


export class AuthorizationService {
  private accessToken: string | null;
  private maxAge: number;
  static readonly serviceName: string = 'AuthorizationService';

  public constructor() {
    this.accessToken = this.getAccessToken();
    this.maxAge = 0;

    this.setAuthorizationToken(this.accessToken);
  }
  public init=() :void => {
    axios.interceptors.response.use(
      response => {
        if (response && response.data) {
          //setting guest token
          if (response.data.token && response.data.token.access_token && response.data.token.expires_in
            && this.accessToken !== response.data.token.access_token) {
            this.saveToken(response.data.token);
            //check if it is ANONYMOUS token
            if (response.data.token.scope) {
              this.findAndSaveAnonymousId(response.data.token.scope);
            }
          }
        }

        return response;
      },
      error => {
        if (error.response && error.response.data) {
          //setting guest token
          if (error.response.data.token && error.response.data.token.access_token && error.response.data.token.expires_in) {
            this.saveToken(error.response.data.token);
            //check if it is ANONYMOUS token
            if (error.response.data.token.scope) {
              this.findAndSaveAnonymousId(error.response.data.token.scope);
            }
          }
        }

        return Promise.reject(error);
      }
    );
  };
  public findAndSaveAnonymousId = (scope:string) : void => {
    const anonymousId = scope.split(' ').
      map(a => a.split(':')).find(a => a[0]=='anonymous_id');
    if (anonymousId !== undefined) {
      appLocalStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, anonymousId[1]);
    }
  };

  public saveToken = (token:Token): void => {
    this.maxAge = Number(token.expires_in);
    this.accessToken = token.access_token;
    this.setAuthorizationToken(this.accessToken);
    this.storeAccessToken();
  };
  public authorizeByPassword = async (
    email: string,
    password: string,
    mergeCarts = true,
    options: { channel?: string; state?: string } = {}
  ) : Promise<LogInResult> => {
    try {

      if (mergeCarts) {
        //anonymousId = appLocalStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
      }

      const loginParams: Record<string, string> = {};
      if (options.channel) loginParams.channel = options.channel;
      if (options.state) loginParams.state = options.state;

      const { data: { customer, cart } } = await axios.post(
        `${API_HOST_AUTH}/login`, { email, password }, { params: loginParams }
      );
      const { data: { token } } = await axios.post(
        `${API_HOST_AUTH}/token`, { email, password }, { auth: { username: email, password } }
      );
      appLocalStorage.removeItem(ANONYMOUS_ID_STORAGE_KEY);

      this.accessToken = token.access_token ?? token;
      this.maxAge = token.expires_in;

      this.setAuthorizationToken(this.accessToken);
      this.storeAccessToken();

      return {
        customer: customer as Customer,
        cart: cart ? mapFullCart(cart) : null
      };
    } catch (err: unknown) {
      handleServiceError(err, { showSnackbar: false });
      const { response } = err as AxiosError<{ code?: string; message?: string }>;
      const errorCode = response?.data?.code || 'UNKNOWN_ERROR';
      const errorMessage = response?.data?.message || 'Something went wrong';

      return { errorCode, errorMessage };
    }
  };

  public signUp = async (customerDraft: CustomerDraft): Promise<LogInResult> => {
    try {
      const anonymousId = appLocalStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);

      const { data: { customer, cart } } = await axios.post(`${API_HOST_AUTH}/signup`, { ...customerDraft, anonymousId });
      const { data: { token } } = await axios.post(
        `${API_HOST_AUTH}/token`, { email: customerDraft.email, password: customerDraft.password },
        { auth: { username: customerDraft.email!, password: customerDraft.password! } }
      );
      appLocalStorage.removeItem(ANONYMOUS_ID_STORAGE_KEY);
      this.accessToken = token.access_token ?? token;
      this.maxAge = token.expires_in;
      this.setAuthorizationToken(this.accessToken);
      this.storeAccessToken();

      return {
        customer: customer as Customer,
        cart: cart ? mapFullCart(cart) : null
      };
    } catch (err) {
      handleServiceError(err, { showSnackbar: false });
      const { response } = err as AxiosError<{ code?: string; message?: string }>;
      const errorCode = response?.data?.code || 'UNKNOWN_ERROR';
      const errorMessage = response?.data?.message || 'Something went wrong';

      return { errorCode, errorMessage };
    }
  };

  public signOut = (): void => {
    this.removeAccessToken();
    this.removeAuthorizationToken();
  };

  public getSignedInCustomer = async (): Promise<Customer | null> => {
    try {
      const { data: customer } = await axios.get(API_HOST_AUTH!);

      return customer;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  };

  private storeAccessToken(): void {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.cookie = `${ACCESS_TOKEN_COOKIE}=${this.accessToken};max-age=${this.maxAge};path=/`;
    } else {
      cookies = `${ACCESS_TOKEN_COOKIE}=${this.accessToken};max-age=${this.maxAge};path=/`;
    }
  }

  public getAccessToken = (): string | null => {
    const accessTokenRegexp = new RegExp(`${ACCESS_TOKEN_COOKIE}=([^;]*)`);
    const matchedCookie = typeof window !== 'undefined' && typeof document !== 'undefined' ?
      document.cookie.match(accessTokenRegexp) : cookies.match(accessTokenRegexp);

    if (matchedCookie && matchedCookie.length > 1) {
      const [ ,accessTokenValue ] = matchedCookie;

      return accessTokenValue;
    }

    return null;
  };

  public removeAccessToken = (): void => {
    this.accessToken = null;
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.cookie = `${ACCESS_TOKEN_COOKIE}= ;path=/; expires = Thu, 01 Jan 1970 00:00:00 GMT`;
    } else {
      cookies = `${ACCESS_TOKEN_COOKIE}= ;path=/; expires = Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  };

  private setAuthorizationToken = (accessToken?: string | null): void => {
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
  };

  private removeAuthorizationToken = (): void => {
    axios.defaults.headers.common['Authorization'] = '';
  };

  public updateName = async (version: number, name: { firstName?: string, lastName?: string }): Promise<Customer | null> => {
    try {
      const actions: ({ action: 'setFirstName', firstName: string } | { action: 'setLastName', lastName: string })[] = [];

      if (name.firstName) {
        actions.push({ action: 'setFirstName', firstName: name.firstName });
      }

      if (name.lastName) {
        actions.push({ action: 'setLastName', lastName: name.lastName });
      }

      const { data: customer } = await axios.post(API_HOST_AUTH!, { version, actions });

      return customer;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  };

  public updateAddressAndSetDefaultAddress = async (
    version: number,
    address: Partial<AddressDraft>,
    addressId?: string
  ): Promise<Customer | null> => {
    try {
      let customer: Customer | null;
      let defaultAddressId: string;
      if (addressId) {
        customer = await this.changeAddress(version, address, addressId);
        defaultAddressId = addressId;
      } else {
        customer = await this.addAddress(version, address);
        // for now we only have one address that we update
        defaultAddressId = customer?.addresses[0].id as string;
      }

      return this.setDefaultShippingAddress(customer?.version as number, defaultAddressId);
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  };

  public addAddress = async (version: number, address: Partial<AddressDraft>): Promise<Customer | null> => {
    try {
      const actions = [ { action: 'addAddress', address: { ...address, country: 'US' } } ];

      const { data: customer } = await axios.post(API_HOST_AUTH!, { version, actions });

      return customer;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  };

  public changeAddress = async (version: number, address: Partial<AddressDraft>, addressId: string): Promise<Customer | null> => {
    try {
      const actions = [ { action: 'changeAddress', addressId, address: { ...address, country: 'US' } } ];

      const { data: customer } = await axios.post(API_HOST_AUTH!, { version, actions });

      return customer;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  };

  public setDefaultShippingAddress = async (version: number, addressId: string): Promise<Customer | null> => {
    try {
      const actions = [ { action: 'setDefaultShippingAddress', addressId } ];

      const { data: customer } = await axios.post(API_HOST_AUTH!, { version, actions });

      return customer;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  };
}
